/**
 * Game Table Screen
 * Wrapper that fetches initial table data via REST before rendering the WebSocket-connected GameTable.
 * Shows loading/error states and provides a "Leave Table" button in the header.
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { gameService, GameTable as GameTableData } from '../../services/gameService';
import { useAuth } from '../../contexts/AuthContext';
import LoadingSpinner from '../shared/LoadingSpinner';
import ErrorState from '../shared/ErrorState';
import SoundToggle from '../shared/SoundToggle';
import GameTable from './GameTable';
import { useGameStore } from '../../store/gameStore';

interface GameTableScreenProps {
  tableId: string;
}

const GameTableScreen: React.FC<GameTableScreenProps> = ({ tableId }) => {
  // The header follows the live game: the table as fetched when the page
  // opened goes stale (it said WAITING all through a game).
  // Until this table's live game has arrived, the table as fetched.
  const live = useGameStore((s) => s.tableId === tableId);
  const liveSeated = useGameStore((s) => s.players.length);
  const phase = useGameStore((s) => s.phase);
  const over = useGameStore((s) => s.tableOver !== null);
  const ante = useGameStore((s) => s.ante?.current);
  const [tableData, setTableData] = useState<GameTableData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { token } = useAuth();
  const navigate = useNavigate();

  const fetchTable = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await gameService.getTable(tableId);
      setTableData(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load table');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchTable();
  }, [tableId]);

  const handleLeaveTable = async () => {
    try {
      await gameService.leaveTable(tableId);
      navigate('/lobby');
    } catch (err: any) {
      console.error('Failed to leave table:', err);
      // Navigate anyway — user wants to leave
      navigate('/lobby');
    }
  };

  if (isLoading) {
    return <LoadingSpinner fullScreen message="Loading table..." />;
  }

  if (error || !tableData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 flex items-center justify-center p-4">
        <div className="max-w-md w-full">
          <ErrorState
            dark
            message={error || 'Table not found'}
            onRetry={fetchTable}
          />
          <div className="mt-6 flex justify-center">
            <button
              onClick={() => navigate('/lobby')}
              className="px-6 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg font-semibold transition-colors"
            >
              Back to Lobby
            </button>
          </div>
        </div>
      </div>
    );
  }

  const seated = live ? liveSeated : tableData.currentPlayers;

  return (
    // On a computer the table fits the window under the header (no page scroll).
    <div className="desk:h-dvh desk:flex desk:flex-col">
      {/* Header with table info and leave button. Not on phones: there the
          game fills the whole screen (the header covered the top of the
          grid), and the Lobby button between hands is the way out. */}
      <div className="bg-gray-900/90 border-b border-gray-700 px-4 py-3 sticky top-0 z-50 backdrop-blur-sm portrait:hidden short:hidden">
        <div className="max-w-[1800px] mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/lobby')}
              className="text-gray-400 hover:text-white transition-colors"
              title="Back to Lobby"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
            </button>
            <div>
              <h1 className="text-lg font-bold text-white">{tableData.name}</h1>
              <p className="text-xs text-gray-400">
                Ante: {ante ?? tableData.anteAmount} &middot; Players: {seated}/{tableData.maxPlayers}
              </p>
            </div>
            {(() => {
              const [label, tone] =
                !live
                  ? [tableData.status, tableData.status === 'ACTIVE' ? 'bg-green-900 text-green-300' : 'bg-yellow-900 text-yellow-300']
                  : seated === 0
                  ? ['WAITING', 'bg-yellow-900 text-yellow-300']
                  : over
                    ? ['GAME OVER', 'bg-gray-700 text-gray-300']
                    : phase === 'ENDED' || phase === 'ANTE'
                      ? ['BETWEEN HANDS', 'bg-yellow-900 text-yellow-300']
                      : ['IN PLAY', 'bg-green-900 text-green-300'];
              return <span className={`px-2 py-1 text-xs font-semibold rounded ${tone}`}>{label}</span>;
            })()}
          </div>
          <div className="flex items-center gap-2">
            <SoundToggle />
            <button
              onClick={handleLeaveTable}
              className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-semibold transition-colors text-sm"
            >
              Leave Table
            </button>
          </div>
        </div>
      </div>

      {/* Main game table (WebSocket-connected) */}
      {token && <GameTable tableId={tableId} token={token} tableData={tableData} />}
    </div>
  );
};

export default GameTableScreen;
